import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import type { Question, Answer } from "@/types/api";
import type {
  AssessmentState,
  UIAnswer,
  FeedbackState,
  AnswerRecord,
} from "@/types/assessment";
import {
  convertAnswerForUI,
  isSingleChoiceHiddenAnswer,
  isMultiSelectHiddenAnswer,
} from "@/types/assessment";

// Helper to extract correctOptions from hidden_answer
function extractCorrectOptions(
  questionType: string,
  hiddenAnswer?: Record<string, unknown>
): string[] | undefined {
  if (!hiddenAnswer) return undefined;

  if (questionType === "single_choice" && isSingleChoiceHiddenAnswer(hiddenAnswer)) {
    return [String(hiddenAnswer.correct_option_id)];
  }
  if (questionType === "multi_select" && isMultiSelectHiddenAnswer(hiddenAnswer)) {
    return hiddenAnswer.options
      .filter((opt) => opt.is_correct)
      .map((opt) => String(opt.option_id));
  }
  return undefined;
}

// Using Record instead of Map for Redux DevTools compatibility and serialization
const initialState = {
  attemptId: null,
  assessmentId: null,
  assessmentTitle: "",
  questions: [],
  currentIndex: 0,
  currentAnswer: null,
  isSubmitting: false,
  submittedAnswers: {} as Record<number, AnswerRecord>,
  feedback: null,
  showFeedback: false,
  isCompleted: false,
  finalScore: null,
  isReviewMode: false,
  hiddenAnswers: {} as Record<number, Record<string, unknown>>,
  videoExplanations: {} as Record<number, string>,
  drafts: {} as Record<number, unknown>,
};

export const useAssessmentStore = create<AssessmentState>((set, get) => ({
  ...initialState,

  initAssessment: (
    attemptId: number,
    assessmentId: number,
    title: string,
    questions: Question[],
    existingAnswers: Answer[] = [],
    hiddenAnswers: Record<number, Record<string, unknown>> = {},
    drafts: Record<number, unknown> = {}
  ) => {
    // Build set of answered question IDs
    const answeredQuestionIds = new Set(existingAnswers.map((a) => a.question_id));

    // Build submitted answers record from existing answers
    const submittedAnswers: Record<number, AnswerRecord> = {};
    for (const answer of existingAnswers) {
      const question = questions.find((q) => q.id === answer.question_id);
      if (question) {
        const hiddenAnswer = hiddenAnswers[question.id];
        submittedAnswers[answer.question_id] = {
          questionId: answer.question_id,
          answer: convertAnswerForUI(question.type, answer.answer),
          feedback: {
            isCorrect: answer.is_correct,
            explanation: answer.feedback,
            correctOptions: extractCorrectOptions(question.type, hiddenAnswer),
          },
          submittedAt: new Date(),
        };
      }
    }

    // Find first unanswered question index
    const firstUnansweredIndex = questions.findIndex(
      (q) => !answeredQuestionIds.has(q.id)
    );
    // If all answered, stay at last question (will show completion)
    const startIndex = firstUnansweredIndex === -1 ? questions.length : firstUnansweredIndex;

    // Check if all questions are already answered
    const isCompleted = startIndex >= questions.length;

    // Restore current answer from draft if the starting question has one
    const startQuestion = questions[isCompleted ? questions.length - 1 : startIndex];
    const draft = startQuestion && drafts[startQuestion.id];
    const draftAnswer = draft ? convertAnswerForUI(startQuestion.type, draft) : null;

    set({
      ...initialState,
      attemptId,
      assessmentId,
      assessmentTitle: title,
      questions,
      currentIndex: isCompleted ? questions.length - 1 : startIndex,
      currentAnswer: draftAnswer,
      submittedAnswers,
      hiddenAnswers,
      drafts,
      isCompleted,
    });
  },

  setCurrentAnswer: (answer: UIAnswer) => {
    set({ currentAnswer: answer });
  },

  setSubmitting: (isSubmitting: boolean) => {
    set({ isSubmitting });
  },

  setFeedback: (feedback: FeedbackState, answerRecord: AnswerRecord) => {
    const { submittedAnswers, drafts } = get();
    const newDrafts = { ...drafts };
    delete newDrafts[answerRecord.questionId];

    set({
      feedback,
      showFeedback: true,
      submittedAnswers: {
        ...submittedAnswers,
        [answerRecord.questionId]: answerRecord,
      },
      drafts: newDrafts,
    });
  },

  setHiddenAnswer: (questionId: number, hiddenAnswer: Record<string, unknown>) => {
    const { hiddenAnswers } = get();
    set({
      hiddenAnswers: {
        ...hiddenAnswers,
        [questionId]: hiddenAnswer,
      },
    });
  },

  setVideoExplanation: (questionId: number, url: string) => {
    const { videoExplanations } = get();
    set({
      videoExplanations: {
        ...videoExplanations,
        [questionId]: url,
      },
    });
  },

  nextQuestion: () => {
    const { currentIndex, questions, submittedAnswers, drafts } = get();
    const nextIndex = currentIndex + 1;

    if (nextIndex >= questions.length) {
      return;
    }

    const nextQ = questions[nextIndex];
    const nextAnswer = submittedAnswers[nextQ?.id];
    const draft = !nextAnswer && nextQ && drafts[nextQ.id]
      ? convertAnswerForUI(nextQ.type, drafts[nextQ.id])
      : null;

    set({
      currentIndex: nextIndex,
      currentAnswer: nextAnswer?.answer ?? draft ?? null,
      feedback: nextAnswer?.feedback ?? null,
      showFeedback: Boolean(nextAnswer),
    });
  },

  previousQuestion: () => {
    const { currentIndex, submittedAnswers, questions, drafts } = get();

    if (currentIndex <= 0) return;

    const prevIndex = currentIndex - 1;
    const prevQ = questions[prevIndex];
    const prevAnswer = submittedAnswers[prevQ?.id];
    const draft = !prevAnswer && prevQ && drafts[prevQ.id]
      ? convertAnswerForUI(prevQ.type, drafts[prevQ.id])
      : null;

    set({
      currentIndex: prevIndex,
      currentAnswer: prevAnswer?.answer ?? draft ?? null,
      feedback: prevAnswer?.feedback ?? null,
      showFeedback: Boolean(prevAnswer),
    });
  },

  goToQuestion: (index: number) => {
    const { questions, submittedAnswers, drafts } = get();

    if (index < 0 || index >= questions.length) return;

    const targetQ = questions[index];
    const targetAnswer = submittedAnswers[targetQ?.id];
    const draft = !targetAnswer && targetQ && drafts[targetQ.id]
      ? convertAnswerForUI(targetQ.type, drafts[targetQ.id])
      : null;

    set({
      currentIndex: index,
      currentAnswer: targetAnswer?.answer ?? draft ?? null,
      feedback: targetAnswer?.feedback ?? null,
      showFeedback: Boolean(targetAnswer),
    });
  },

  initReviewMode: (
    attemptId: number,
    assessmentId: number,
    title: string,
    questions: Question[],
    answers: Answer[],
    hiddenAnswers: Record<number, Record<string, unknown>>,
    score: number
  ) => {
    // Build submitted answers from all answers
    const submittedAnswers: Record<number, AnswerRecord> = {};

    for (const answer of answers) {
      const question = questions.find((q) => q.id === answer.question_id);
      if (question) {
        const hiddenAnswer = hiddenAnswers[question.id];
        submittedAnswers[answer.question_id] = {
          questionId: answer.question_id,
          answer: convertAnswerForUI(question.type, answer.answer),
          feedback: {
            isCorrect: answer.is_correct,
            explanation: answer.feedback,
            correctOptions: extractCorrectOptions(question.type, hiddenAnswer),
          },
          submittedAt: new Date(),
        };
      }
    }

    // Get first question's answer for initial display
    const firstQuestion = questions[0];
    const firstAnswer = firstQuestion ? submittedAnswers[firstQuestion.id] : null;

    set({
      ...initialState,
      attemptId,
      assessmentId,
      assessmentTitle: title,
      questions,
      currentIndex: 0,
      submittedAnswers,
      hiddenAnswers,
      isReviewMode: true,
      isCompleted: false, // Not showing results yet
      finalScore: score,
      // Show first question in review state
      currentAnswer: firstAnswer?.answer ?? null,
      feedback: firstAnswer?.feedback ?? null,
      showFeedback: true,
    });
  },

  completeAssessment: (score: number) => {
    set({
      isCompleted: true,
      finalScore: score,
    });
  },

  reset: () => {
    set({
      ...initialState,
      submittedAnswers: {},
    });
  },
}));

// Selector hooks for common derived state
export const useCurrentQuestion = () =>
  useAssessmentStore((state) =>
    state.questions[state.currentIndex] ?? null
  );

export const useProgress = () =>
  useAssessmentStore(
    useShallow((state) => ({
      current: state.currentIndex + 1,
      total: state.questions.length,
      percentage:
        state.questions.length > 0
          ? ((state.currentIndex + 1) / state.questions.length) * 100
          : 0,
    }))
  );

export const useCanSubmit = () =>
  useAssessmentStore((state) => {
    if (state.isSubmitting || state.showFeedback) return false;
    if (state.currentAnswer === null) return false;

    // For arrays (multi-select), check if at least one selected
    if (Array.isArray(state.currentAnswer)) {
      return state.currentAnswer.length > 0;
    }

    // For strings, check non-empty
    if (typeof state.currentAnswer === "string") {
      return state.currentAnswer.trim().length > 0;
    }

    return true;
  });

export const useIsLastQuestion = () =>
  useAssessmentStore(
    (state) => state.currentIndex === state.questions.length - 1
  );

export const useIsFirstQuestion = () =>
  useAssessmentStore((state) => state.currentIndex === 0);

export const useCanNavigate = () =>
  useAssessmentStore(
    useShallow((state) => {
      const currentQuestion = state.questions[state.currentIndex];
      const isCurrentAnswered = currentQuestion && state.submittedAnswers[currentQuestion.id];

      return {
        canGoPrevious: state.currentIndex > 0 && (Boolean(isCurrentAnswered) || state.isReviewMode),
        canGoNext: state.currentIndex < state.questions.length - 1,
        isReviewMode: state.isReviewMode,
      };
    })
  );

export const useHiddenAnswerForQuestion = (questionId: number) =>
  useAssessmentStore((state) => state.hiddenAnswers[questionId]);

export const useVideoExplanation = (questionId: number) =>
  useAssessmentStore((state) => state.videoExplanations[questionId]);
